// Centralized SEO config for NivaLabs.ai (canonical: apex domain)
export const SITE = {
  siteName: "NivaLabs AI",
  siteUrl: "https://nivalabs.ai",
  logo: "https://nivalabs.ai/logos/nivalabs-on-light.png",
  defaultOgImage: "https://nivalabs.ai/og/default.png",
  defaultOgImageWidth: 1200,
  defaultOgImageHeight: 630,
  twitter: "@nivalabs_ai",
  locale: "en_US",
  /**
   * Social and brand profiles (Organization sameAs) — not geo-targeting signals.
   */
  sameAs: [
    "https://www.linkedin.com/company/nivalabs-ai/",
    "https://www.instagram.com/nivalabs.ai/",
    "https://www.facebook.com/people/Nivalabs-AI/61575541688397/",
  ],
};

const SEOConfig = {
  home: {
    pageTitle: "AI Systems for Real Business Operations",
    pageDescription:
      "Nivalabs builds production AI agents, custom LLM apps, workflow automation, and integrations for logistics, aviation, marine, and enterprise operations.",
    keywords:
      "AI development company, enterprise AI, AI agent development, custom LLM development, AI workflow automation, AI integration, Nivalabs",
    ogTitle: "Nivalabs AI | AI Systems for Real Business Operations",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/`,
  },

  aiAgentDevelopment: {
    pageTitle: "AI Agent Development",
    pageDescription:
      "Production AI agents from Nivalabs: multi-step reasoning, tool and API use, system integration, and human-in-the-loop workflows for operations teams.",
    keywords:
      "AI agent development, autonomous AI agents, enterprise AI agents, LLM agents, AI automation agents, business process agents, Nivalabs",
    ogTitle: "AI Agent Development | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/ai-agent-development`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/ai-agent-development`,
  },

  aiIntegrations: {
    pageTitle: "AI Integrations for Business Systems",
    pageDescription:
      "Integrate AI into ERP, CRM, and internal platforms without replacing your stack: secure APIs, data connectors, and LLM features that fit existing workflows.",
    keywords:
      "AI integration, enterprise AI integration, ERP AI, CRM AI, API integration, LLM integration, system integration, Nivalabs",
    ogTitle: "AI Integrations for ERP, CRM & Business Systems | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/ai-integrations`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/ai-integrations`,
  },

  aiWorkflowAutomation: {
    pageTitle: "AI Workflow Automation",
    pageDescription:
      "Intelligent workflow automation combining LLMs, rules, and integrations to cut manual work, shorten cycle times, and improve quality in day-to-day operations.",
    keywords:
      "AI workflow automation, intelligent process automation, LLM workflows, business workflow automation, operational AI, Nivalabs",
    ogTitle: "AI Workflow Automation | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/ai-workflow-automation`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/ai-workflow-automation`,
  },

  customLlmApplicationDevelopment: {
    pageTitle: "Custom LLM Application Development",
    pageDescription:
      "Custom LLM applications: retrieval-augmented generation, domain assistants, evaluation, and production deployment for operations-heavy environments.",
    keywords:
      "custom LLM development, LLM application development, RAG development, enterprise LLM, fine-tuned models, AI application development, Nivalabs",
    ogTitle: "Custom LLM Application Development | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/custom-llm-application-development`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/custom-llm-application-development`,
  },

  mvpPocDevelopment: {
    pageTitle: "AI MVP & PoC Development",
    pageDescription:
      "Rapid AI proof-of-concept and MVP builds to validate use cases, data and system fit, and ROI before committing to full-scale production engineering.",
    keywords:
      "AI MVP development, AI proof of concept, AI PoC, LLM prototype, AI pilot, minimum viable product AI, Nivalabs",
    ogTitle: "AI MVP & PoC Development | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/mvp-poc-development`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/mvp-poc-development`,
  },

  blog: {
    pageTitle: "Insights on AI, Agents & LLM Systems",
    pageDescription:
      "Articles from Nivalabs on AI agents, LLM applications, workflow automation, integrations, and shipping production AI for real operations.",
    keywords:
      "AI blog, AI agents, LLM engineering, AI automation, enterprise AI, Nivalabs blog, AI integration",
    ogTitle: "Insights on AI, Agents & LLM Systems | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/blogs`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/blogs`,
  },

  contact: {
    pageTitle: "Contact Our AI Engineering Team",
    pageDescription:
      "Contact Nivalabs to discuss AI agents, LLM applications, workflow automation, or integrations for your operations.",
    keywords:
      "contact Nivalabs, AI consulting, AI agents, enterprise AI, workflow automation",
    ogTitle: "Contact Us | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/contact`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/contact`,
  },

  solutions: {
    pageTitle: "AI Solutions for Operations",
    pageDescription:
      "Browse Nivalabs AI solutions for enterprise chatbots, knowledge assistants, logistics, marina operations, and data intelligence platforms.",
    keywords:
      "AI solutions, enterprise chatbot, knowledge assistant, logistics AI, marina AI, data intelligence, Nivalabs",
    ogTitle: "AI Solutions for Operations | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/solutions`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/solutions`,
  },

  about: {
    pageTitle: "About Nivalabs: AI Engineering for Operations",
    pageDescription:
      "Nivalabs is a PySquad AI engineering company building agents, custom LLM applications, workflow automation, and integrations operators can own in production.",
    keywords:
      "about Nivalabs AI, AI company, AI engineering team, AI agents, LLM development, workflow automation, PySquad, Nivalabs",
    ogTitle: "About Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/about`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/about`,
  },

  howWeWork: {
    pageTitle: "How We Work: Our AI Delivery Process",
    pageDescription:
      "How Nivalabs runs AI engagements: discovery, design, pilot, and production handoff for operations teams.",
    keywords:
      "AI delivery process, AI pilot, AI engagement model, enterprise AI delivery, Nivalabs",
    ogTitle: "How We Work | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/how-we-work`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/how-we-work`,
  },

  useCases: {
    pageTitle: "AI Use Cases for Operations",
    pageDescription:
      "Practical AI use cases Nivalabs builds: document workflows, knowledge assistants, agents, integrations, logistics, and marina operations.",
    keywords:
      "AI use cases, enterprise AI examples, AI agents use cases, LLM applications, workflow automation, Nivalabs",
    ogTitle: "AI Use Cases for Operations | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/use-cases`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/use-cases`,
  },

  faq: {
    pageTitle: "AI Development FAQ",
    pageDescription:
      "Frequently asked questions about working with Nivalabs: delivery, security, fit, pilots, and how engagements start.",
    keywords:
      "Nivalabs FAQ, AI consulting questions, AI pilot FAQ, enterprise AI delivery",
    ogTitle: "FAQ | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/faq`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/faq`,
  },

  privacy: {
    pageTitle: "Privacy",
    pageDescription:
      "How Nivalabs handles information collected through nivalabs.ai and contact channels.",
    keywords: "Nivalabs privacy, privacy policy, data protection",
    ogTitle: "Privacy | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/privacy`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/privacy`,
  },

  terms: {
    pageTitle: "Terms of Use",
    pageDescription:
      "Terms of use for the Nivalabs website. Paid engagements are governed by separate agreements.",
    keywords: "Nivalabs terms, terms of use, website terms",
    ogTitle: "Terms of Use | Nivalabs AI",
    ogImage: SITE.defaultOgImage,
    ogUrl: `${SITE.siteUrl}/terms`,
    ogType: "website",
    locale: SITE.locale,
    canonical: `${SITE.siteUrl}/terms`,
  },

  default: {
    pageTitle: "NivaLabs AI",
    pageDescription:
      "Nivalabs helps enterprises ship production AI: agents, LLM apps, workflow automation, and integrations for operations-heavy teams.",
    keywords:
      "AI automation, enterprise AI, AI agents, LLM applications, workflow automation, machine learning, AI consulting, Nivalabs",
    ogTitle: "NivaLabs AI | AI Systems for Real Business Operations",
    ogImage: SITE.defaultOgImage,
    ogUrl: SITE.siteUrl,
    ogType: "website",
    locale: SITE.locale,
    canonical: SITE.siteUrl,
  },
};

export default SEOConfig;
